/**
 * 글 + 답글 (시안 05 S2a · S2b · S1b-c, D46). 고민 글과 상회칠판 글이 같이 씁니다.
 *
 * - 글: 표식 + 이름 / 「어디에 올림 · 시간」 + 종류 칩 → 본문 → 「답글 {n} · 멤버 {m}명이 답했어요」
 * - 답글(맨 위): 표식 36 + 이름 14/600 + 시간 → 본문(멤버 마루 부리 16/27) → 행동 줄 「답글」 · 「{멤버}와 1:1로 이어 가기」
 * - 대댓글: 한 단계 들여쓰기(왼쪽 12 + 1px 세로선), 표식 28 + 이름 13/600 + 시간 11 → 15/24(내 말은 IBM Plex, 멤버 말은 마루 부리)
 * - 쓰는 중: 표식 28 + 점 3개 상자 + 「{멤버} 답글 쓰는 중」
 * - 입력창: 대상이 있으면 위에 띠(「{멤버}에게 답글 다는 중」 + ×)와 테두리 1.5px --brand
 */
import { useEffect, useRef, useState } from 'react';
import { CornerDownRight, MessageCircle, X } from 'lucide-react';
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';
import { Avatar } from '../Avatar.jsx';
import { formatClock } from '../../pages/board/boardFeed.js';

export function memberNameOf(memberKey) {
    return getMember(memberKey)?.name ?? '';
}

/** 단골 표식: 브랜드 원 + 호칭 첫 글자 */
export function MyMark({ name, size = 36 }) {
    return (
        <span
            aria-hidden="true"
            className="flex shrink-0 items-center justify-center rounded-full bg-brand font-bold text-on-brand"
            style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
        >
            {Array.from(name ?? '')[0] ?? ''}
        </span>
    );
}

function Mark({ reply, displayName, size }) {
    if (reply.sender === 'user') {
        return <MyMark name={displayName} size={size} />;
    }
    return <Avatar memberKey={reply.memberKey} size={size} />;
}

function authorName(reply, displayName) {
    return reply.sender === 'user' ? displayName : memberNameOf(reply.memberKey);
}

const KIND_CHIP = {
    worry: { label: '고민', className: 'border border-accent text-accent-text' },
    day: { label: '일상', className: 'border border-[rgba(38,35,31,0.16)] text-text-secondary' },
    content: { label: '매일 콘텐츠', className: 'bg-[#F6E3C8] text-accent-text' },
};

export function KindChip({ kind }) {
    const chip = KIND_CHIP[kind] ?? KIND_CHIP.day;
    return (
        <span
            className={`inline-flex h-[26px] shrink-0 items-center rounded-[6px] px-2 text-[12px] font-semibold ${chip.className}`}
        >
            {chip.label}
        </span>
    );
}

/** 글 머리 (S2a) */
export function ThreadPost({ mark, name, meta, kind, body, replyCount, memberCount, edited, serif = false }) {
    return (
        <article className="flex flex-col gap-3 border-b border-hairline bg-surface p-4">
            <div className="flex items-center gap-2.5">
                {mark}
                <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-[15px] font-semibold">{name}</span>
                    <span className="text-[12px] text-text-secondary">
                        {meta}
                        {edited ? ' · 고침' : ''}
                    </span>
                </div>
                <KindChip kind={kind} />
            </div>
            <p
                className={`m-0 break-words whitespace-pre-wrap ${
                    serif ? 'font-serif text-[17px] leading-[29px]' : 'text-[17px] leading-[28px]'
                }`}
            >
                {body}
            </p>
            <span className="text-[12px] text-label-muted">
                답글 {replyCount}
                {memberCount ? ` · 멤버 ${memberCount}명이 답했어요` : ''}
            </span>
        </article>
    );
}

function Typing({ memberKey }) {
    return (
        <div className="flex items-center gap-2 py-1.5" aria-live="polite">
            <Avatar memberKey={memberKey} size={28} />
            <span
                className="flex h-[30px] items-center gap-1 rounded-[15px] bg-surface-sunken px-2.5"
                aria-hidden="true"
            >
                {[0, 1, 2].map((dot) => (
                    <span
                        key={dot}
                        className="size-1.5 rounded-full bg-text-secondary motion-safe:animate-typing-dot"
                        style={{ animationDelay: `${dot * 160}ms` }}
                    />
                ))}
            </span>
            <span className="text-[12px] text-text-secondary">{memberNameOf(memberKey)} 답글 쓰는 중</span>
        </div>
    );
}

function NestedReply({ reply, displayName, authorKey, onChat }) {
    const isUser = reply.sender === 'user';
    return (
        <div className="flex items-start gap-2 py-1.5">
            <Mark reply={reply} displayName={displayName} size={28} />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-1.5">
                    <span className="text-[13px] font-semibold">{authorName(reply, displayName)}</span>
                    {authorKey && reply.memberKey === authorKey ? <AuthorBadge /> : null}
                    <span className="text-[11px] text-text-secondary">{formatClock(reply.createdAt)}</span>
                </span>
                <p
                    className={`m-0 break-words whitespace-pre-wrap ${
                        isUser ? 'text-[15px] leading-6' : 'font-serif text-[15px] leading-6'
                    }`}
                >
                    {reply.body}
                </p>
                {reply.toChat ? (
                    <button
                        type="button"
                        onClick={() => onChat(reply)}
                        className="mt-1 inline-flex h-9 items-center gap-1.5 self-start rounded-[18px] bg-brand-tint px-3 text-[13px] font-semibold text-brand"
                    >
                        <MessageCircle size={16} strokeWidth={1.8} aria-hidden="true" />
                        {withJosa(memberNameOf(reply.memberKey), '과', '와')} 1:1로 이어 가기
                    </button>
                ) : null}
            </div>
        </div>
    );
}

/** 「글쓴이」 표시 (S1b-c) */
export function AuthorBadge() {
    return (
        <span className="inline-flex h-[18px] items-center rounded-[9px] bg-brand-tint px-1.5 text-[10px] font-bold text-brand">
            글쓴이
        </span>
    );
}

/** 맨 위 답글 + 그 줄 (S2a · S2b) */
export function ReplyLine({ line, displayName, activeTargetId, pending, authorKey = null, onReply, onChat }) {
    const isMember = line.sender === 'member';
    const members = [line, ...line.children].filter((reply) => reply.sender === 'member');
    const chatMember = isMember ? line.memberKey : (members[0]?.memberKey ?? null);
    const lastMemberReply = members.at(-1) ?? null;
    const replyTarget = isMember ? line : lastMemberReply;
    const open = replyTarget && activeTargetId === replyTarget.repliesId;
    const showTyping = pending && pending.parentId === line.repliesId;
    return (
        <section className="border-b border-hairline px-4 pt-4 pb-2">
            <div className="flex items-start gap-2.5">
                <Mark reply={line} displayName={displayName} size={36} />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="flex items-center gap-1.5">
                        <span className="text-[14px] font-semibold">{authorName(line, displayName)}</span>
                        {authorKey && line.memberKey === authorKey ? <AuthorBadge /> : null}
                        <span className="text-[12px] text-text-secondary">{formatClock(line.createdAt)}</span>
                    </span>
                    <p
                        className={`m-0 break-words whitespace-pre-wrap ${
                            isMember ? 'font-serif text-[16px] leading-[27px]' : 'text-[16px] leading-[26px]'
                        }`}
                    >
                        {line.body}
                    </p>
                    {replyTarget || chatMember ? (
                        <div className="flex h-9 items-center gap-3">
                            {replyTarget ? (
                                <button
                                    type="button"
                                    onClick={() => onReply(replyTarget)}
                                    aria-pressed={Boolean(open)}
                                    className={`inline-flex h-8 items-center gap-1 rounded-[16px] px-2 text-[13px] font-semibold ${
                                        open ? 'bg-brand-tint text-brand' : 'text-text-secondary'
                                    }`}
                                >
                                    <CornerDownRight size={16} strokeWidth={1.8} aria-hidden="true" />
                                    답글
                                </button>
                            ) : null}
                            {chatMember ? (
                                <button
                                    type="button"
                                    onClick={() => onChat(replyTarget ?? line)}
                                    className="text-[13px] font-semibold text-brand"
                                >
                                    {withJosa(memberNameOf(chatMember), '과', '와')} 1:1로 이어 가기
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
            {line.children.length || showTyping ? (
                <div className="mt-1 ml-[46px] border-l border-[rgba(38,35,31,0.12)] pl-3">
                    {line.children.map((reply) => (
                        <NestedReply
                            key={reply.repliesId}
                            reply={reply}
                            displayName={displayName}
                            authorKey={authorKey}
                            onChat={onChat}
                        />
                    ))}
                    {showTyping ? <Typing memberKey={pending.memberKey} /> : null}
                </div>
            ) : null}
        </section>
    );
}

/** 맨 아래 입력창 (S2a · S2b). 대상이 있으면 위 띠와 브랜드 테두리 */
export function ThreadComposer({ target, placeholder, disabled, onClear, onSend }) {
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);
    useEffect(() => {
        if (target) {
            inputRef.current?.focus();
        }
    }, [target]);
    const targetName = target ? memberNameOf(target.memberKey) : null;

    async function submit(event) {
        event.preventDefault();
        const value = text.trim();
        if (!value || busy || disabled) {
            return;
        }
        setBusy(true);
        try {
            await onSend(value);
            setText('');
        } finally {
            setBusy(false);
        }
    }

    return (
        <form
            onSubmit={submit}
            className="shrink-0 border-t border-hairline bg-surface-tab pb-[env(safe-area-inset-bottom,0px)]"
        >
            {target ? (
                <div className="flex h-10 items-center gap-1.5 bg-brand-tint pl-4 text-[13px]">
                    <CornerDownRight size={16} strokeWidth={1.8} aria-hidden="true" className="text-brand" />
                    <span className="flex-1">
                        <b>{targetName}</b>에게 답글 다는 중
                    </span>
                    <button
                        type="button"
                        onClick={onClear}
                        aria-label="답글 대상 지우기"
                        className="flex size-10 items-center justify-center"
                    >
                        <X size={16} strokeWidth={1.8} aria-hidden="true" />
                    </button>
                </div>
            ) : null}
            <div className="flex items-end gap-2 px-3 py-2.5">
                <InputBox
                    className={`flex min-w-0 flex-1 rounded-[22px] bg-surface ${
                        target ? 'border-[1.5px] border-brand' : 'border border-hairline'
                    }`}
                >
                    <textarea
                        ref={inputRef}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        maxLength={500}
                        rows={1}
                        placeholder={target ? `${targetName}에게 답글` : placeholder}
                        aria-label={target ? `${targetName}에게 답글` : placeholder}
                        className="max-h-32 min-h-11 flex-1 resize-none rounded-[22px] border-0 bg-transparent px-4 py-2.5 text-[15px] leading-6"
                    />
                </InputBox>
                <button
                    type="submit"
                    disabled={!text.trim() || busy || disabled}
                    className="h-11 shrink-0 rounded-[22px] bg-brand px-4 text-[14px] font-semibold text-on-brand disabled:opacity-40"
                >
                    보내기
                </button>
            </div>
        </form>
    );
}
